import { Slot } from "@radix-ui/react-slot";
import { type VariantProps, cva } from "class-variance-authority";
import * as React from "react";

import { cn } from "@punto/ui/lib/utils";

// `font-sans` is pinned, not inherited: font-display is set on section
// headings, and Portus nests its section action inside the heading itself
// (DetailLayout.Subtitle wraps DownloadZipButton), so "Descargar todo" and
// "Subir documento" came out in Orbikular while every other button stayed on
// IBM Plex. A button label is never a title — see branding.md §5.
const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-full font-sans text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        destructiveOutline:
          "bg-transparent text-destructive border border-destructive hover:bg-destructive/10",
        warning:
          "bg-warning text-warning-foreground shadow-sm hover:bg-warning/90",
        warningOutline:
          "bg-transparent text-warning border border-warning-base hover:bg-warning/10",
        outline:
          "border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground",
        // --secondary equals --background under the PDS, so this needs a
        // border to be visible at all, and hover:bg-accent because
        // hover:bg-secondary/80 is a no-op on that base. See spec §9.
        secondary:
          "border border-input bg-secondary text-secondary-foreground shadow-sm hover:bg-accent",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        // --primary equals --foreground under the PDS, so a resting link
        // would be byte-identical to body text. The PDS Link component
        // carries active/inactive states rather than a colour cue, so the
        // underline has to be permanent. See spec §7.
        link: "text-primary underline underline-offset-4 hover:no-underline",
        linkSecondary:
          "text-muted-foreground underline underline-offset-4 hover:no-underline",
        outlineHover: "border border-transparent hover:border-border",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-full px-3 text-xs",
        lg: "h-10 rounded-full px-8",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  ref?: React.Ref<HTMLButtonElement>;
}

const Button = ({
  className,
  variant,
  size,
  asChild = false,

  ...props
}: ButtonProps) => {
  const Comp = asChild ? Slot : "button";
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
};

Button.displayName = "Button";

export { Button, buttonVariants };
